import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

// ── Card gradient — blue/navy, distinct from the green wallet hero card ──
const CARD_GRADIENT = "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 55%, #2563eb 100%)";
const CARD_SHADOW   = "0 8px 32px rgba(37,99,235,0.35), 0 2px 8px rgba(37,99,235,0.2)";

// ── Features list shown in Coming Soon state ──────────────────────────────
const CARD_FEATURES = [
  { icon: "ri-global-line",        tone: "success", title: "Spend Globally",       desc: "Use online and in-store anywhere in the world" },
  { icon: "ri-flashlight-line",    tone: "info",    title: "Instant Top-up",       desc: "Fund your card from your wallet in seconds" },
  { icon: "ri-lock-2-line",        tone: "purple",  title: "Freeze & Unfreeze",    desc: "Full control — lock your card any time" },
  { icon: "ri-price-tag-3-line",   tone: "warning", title: "Zero Hidden Fees",     desc: "Transparent pricing, no surprises ever" },
];

// ── MasterCard SVG logo ───────────────────────────────────────────────────
function MastercardLogo() {
  return (
    <svg width="52" height="32" viewBox="0 0 52 32" fill="none">
      <circle cx="19" cy="16" r="14" fill="#EB001B" fillOpacity="0.9" />
      <circle cx="33" cy="16" r="14" fill="#F79E1B" fillOpacity="0.9" />
      <path d="M26 4.8a14 14 0 0 1 0 22.4A14 14 0 0 1 26 4.8z" fill="#FF5F00" fillOpacity="0.9" />
    </svg>
  );
}

// ── The physical card visual ──────────────────────────────────────────────
function VirtualCardFace({ card, masked = true }) {
  const [showDetails, setShowDetails] = useState(false);
  const number = card?.cardNumber || "0000000000000000";
  const display = showDetails
    ? number.replace(/(\d{4})/g, "$1 ").trim()
    : `•••• •••• •••• ${number.slice(-4)}`;

  return (
    <div className="rounded-[20px] relative overflow-hidden p-6 min-h-[200px] flex flex-col justify-between"
      style={{ background: CARD_GRADIENT, boxShadow: CARD_SHADOW }}>
      {/* Decorative sheen circles */}
      <div className="absolute -top-8 -right-8 w-44 h-44 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.08)" }} />
      <div className="absolute -bottom-12 right-10 w-36 h-36 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.05)" }} />

      {/* Card chip */}
      <div className="relative z-10">
        <div className="w-9 h-7 rounded-md mb-4" style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)" }} />
        <button onClick={() => setShowDetails(v => !v)} className="text-left">
          <p className="text-white/85 font-mono text-sm tracking-[0.15em] font-semibold">{display}</p>
        </button>
      </div>

      {/* Card bottom */}
      <div className="relative z-10 flex items-end justify-between mt-4">
        <div>
          <p className="text-white/50 text-[10px] mb-1">Available Balance</p>
          <p className="text-white font-bold text-xl font-display" style={{ fontFeatureSettings: '"tnum"' }}>
            ₦{Number(card?.balance || 0).toLocaleString("en-NG")}
          </p>
          <p className="text-white/55 text-[10px] mt-1 tracking-widest uppercase">
            {card?.cardholderName || "EASWALLET USER"}
          </p>
        </div>
        <MastercardLogo />
      </div>
    </div>
  );
}

// ── Coming Soon placeholder card ──────────────────────────────────────────
function ComingSoonCard() {
  return (
    <div className="rounded-[20px] relative overflow-hidden p-6 min-h-[200px] flex flex-col items-center justify-center text-center"
      style={{ border: "2px dashed var(--color-border)", background: "var(--color-surface-muted)" }}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: "var(--color-info-light)", boxShadow: "inset 0 0 0 1px var(--color-info-ring)" }}>
        <i className="ri-bank-card-line text-2xl" style={{ color: "var(--color-info)" }} />
      </div>
      <p className="text-primary font-bold text-base font-display mb-2">Virtual Card</p>
      <p className="text-muted text-xs leading-relaxed max-w-[220px]">
        Your Easwallet virtual card is coming soon. Join the waitlist to be first in line.
      </p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function CardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // When card issuance launches, swap CARD_COMING_SOON to false
  // and populate `cards` from your card API endpoint.
  const CARD_COMING_SOON = true;

  const [cards,       setCards]       = useState([]);
  const [activeIdx,   setActiveIdx]   = useState(0);
  const [txList,      setTxList]      = useState([]);
  const [txLoading,   setTxLoading]   = useState(false);
  const [frozen,      setFrozen]      = useState(false);
  const [freezing,    setFreezing]    = useState(false);
  const [notified,    setNotified]    = useState(false);
  const [notifying,   setNotifying]   = useState(false);
  const scrollRef = useRef(null);

  // Fetch cards when feature is live (noop in coming-soon state)
  useEffect(() => {
    if (CARD_COMING_SOON) return;
    apiClient.get("/mtrade/cards")
      .then(r => { if (r.data.status) setCards(r.data.data || []); })
      .catch(() => {});
  }, []);

  // Fetch card transactions when a card is selected
  useEffect(() => {
    if (CARD_COMING_SOON || !cards[activeIdx]) return;
    setTxLoading(true);
    apiClient.get(`/mtrade/cards/${cards[activeIdx]._id}/transactions`)
      .then(r => { if (r.data.status) setTxList(r.data.data || []); })
      .catch(() => {})
      .finally(() => setTxLoading(false));
  }, [activeIdx, cards]);

  const handleFreeze = async () => {
    if (freezing || !cards[activeIdx]) return;
    setFreezing(true);
    try {
      await apiClient.post(`/mtrade/cards/${cards[activeIdx]._id}/freeze`, { freeze: !frozen });
      setFrozen(v => !v);
      toast.success(frozen ? "Card unfrozen" : "Card frozen");
    } catch {
      toast.error("Failed to update card status");
    } finally { setFreezing(false); }
  };

  const handleNotify = async () => {
    if (notifying || notified) return;
    setNotifying(true);
    try {
      await apiClient.post("/mtrade/cards/waitlist").catch(() => {});
      setNotified(true);
      toast.success("You're on the waitlist! We'll notify you when cards launch.");
    } catch {
      setNotified(true);
      toast.success("You're on the waitlist!");
    } finally { setNotifying(false); }
  };

  const activeCard = cards[activeIdx] || null;

  return (
    <div className="min-h-screen bg-bg pb-28">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="bg-surface px-5 pt-12 pb-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
        <h1 className="text-primary font-bold text-lg font-display">My Card</h1>
        <button className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
          <i className="ri-settings-3-line text-secondary text-base" />
        </button>
      </div>

      {CARD_COMING_SOON ? (
        /* ── COMING SOON STATE ──────────────────────────────── */
        <>
          {/* Placeholder card */}
          <div className="px-5 pt-4">
            <ComingSoonCard />
          </div>

          {/* Waitlist CTA */}
          <div className="px-5 mt-4">
            <button onClick={handleNotify} disabled={notified || notifying}
              className="w-full py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95"
              style={{ background: notified ? "var(--color-success)" : "var(--color-info)", color: "#fff", opacity: notified ? 0.9 : 1 }}>
              {notifying ? (
                <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Joining…</>
              ) : notified ? (
                <><i className="ri-checkbox-circle-fill" />You're on the waitlist!</>
              ) : (
                <><i className="ri-notification-3-line" />Notify Me When Ready</>
              )}
            </button>
          </div>

          {/* Feature list */}
          <div className="px-5 mt-6">
            <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-4">What's coming</p>
            <div className="space-y-3">
              {CARD_FEATURES.map(f => (
                <div key={f.title}
                  className="flex items-center gap-3 bg-surface border border-border rounded-2xl px-4 py-3.5 shadow-card">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `var(--color-${f.tone}-light)`, boxShadow: `inset 0 0 0 1px var(--color-${f.tone}-ring)` }}>
                    <i className={`${f.icon} text-base`} style={{ color: `var(--color-${f.tone})` }} />
                  </div>
                  <div>
                    <p className="text-primary text-sm font-semibold">{f.title}</p>
                    <p className="text-muted text-xs mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        /* ── CARD EXISTS STATE ──────────────────────────────── */
        <>
          {/* Card carousel */}
          <div className="pt-5 pb-0">
            <div ref={scrollRef}
              className="flex gap-4 overflow-x-auto px-5 pb-2 scrollbar-hide snap-x snap-mandatory"
              onScroll={e => {
                const idx = Math.round(e.target.scrollLeft / (e.target.offsetWidth - 40));
                setActiveIdx(Math.max(0, Math.min(idx, cards.length - 1)));
              }}>
              {cards.length > 0 ? cards.map(card => (
                <div key={card._id} className="snap-center flex-shrink-0 w-full">
                  <VirtualCardFace card={card} />
                </div>
              )) : (
                /* Single demo card when no data yet */
                <div className="snap-center flex-shrink-0 w-full">
                  <VirtualCardFace card={{ cardNumber: "0000000000003245", balance: 0, cardholderName: user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim().toUpperCase() : "EASWALLET USER" }} />
                </div>
              )}
            </div>

            {/* Dot pagination */}
            <div className="flex justify-center gap-2 mt-3 mb-1">
              {Math.max(cards.length, 1) > 1 && [...Array(cards.length)].map((_, i) => (
                <div key={i}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === activeIdx ? "20px" : "7px",
                    background: i === activeIdx ? "var(--color-info)" : "var(--color-border-strong)",
                  }} />
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 px-5 mt-4">
            <button onClick={() => navigate("/internal-transfer")}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 text-white"
              style={{ background: "var(--color-info)" }}>
              <i className="ri-swap-line" /> Transfer
            </button>
            <button onClick={handleFreeze} disabled={freezing}
              className="w-13 h-13 px-4 rounded-2xl border border-border bg-surface shadow-card flex items-center justify-center transition-colors hover:bg-surface-hover"
              style={{ minWidth: "52px" }}>
              {freezing
                ? <div className="w-4 h-4 border-2 border-info border-t-transparent rounded-full animate-spin" />
                : <i className={`${frozen ? "ri-pause-circle-line text-danger" : "ri-snowflake-line text-info"} text-xl`} />}
            </button>
            <button className="w-13 h-13 px-4 rounded-2xl border border-border bg-surface shadow-card flex items-center justify-center hover:bg-surface-hover"
              style={{ minWidth: "52px" }}
              onClick={() => toast.info("Card top-up coming soon")}>
              <i className="ri-add-line text-xl text-secondary" />
            </button>
          </div>

          {/* Drag handle */}
          <div className="flex justify-center mt-4 mb-1">
            <div className="w-10 h-1 rounded-full bg-border" />
          </div>

          {/* Card details + transactions */}
          <div className="bg-surface rounded-t-3xl mt-3 px-5 pt-5 pb-6">
            {/* Status chips */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: "Card Status", value: frozen ? "Frozen" : "Active", color: frozen ? "var(--color-danger)" : "var(--color-success)" },
                { label: "Network",     value: "Visa",   color: "var(--color-info)" },
                { label: "Expires",     value: activeCard?.expiry || "—", color: "var(--color-text-primary)" },
              ].map(s => (
                <div key={s.label} className="bg-surface-muted rounded-xl py-3 text-center border border-border">
                  <p className="text-sm font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-muted text-[10px] mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Card transactions */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-primary font-bold text-sm font-display">Card Transactions</h3>
              <button className="w-8 h-8 rounded-xl bg-surface-muted flex items-center justify-center">
                <i className="ri-search-line text-secondary text-sm" />
              </button>
            </div>

            {txLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 py-3 border-b border-border">
                    <div className="w-10 h-10 rounded-full bg-skeleton animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 bg-skeleton rounded-xl w-28 animate-pulse" />
                      <div className="h-2.5 bg-skeleton rounded-xl w-20 animate-pulse" />
                    </div>
                    <div className="h-3 bg-skeleton rounded-xl w-16 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : txList.length > 0 ? (
              <div className="divide-y divide-border">
                {txList.map(tx => {
                  const initial = tx.merchant?.charAt(0)?.toUpperCase() || "?";
                  const isDebit = tx.type === "debit";
                  return (
                    <div key={tx._id} className="flex items-center gap-3 py-3">
                      <div className="w-11 h-11 rounded-full bg-surface-muted border border-border flex items-center justify-center flex-shrink-0 font-bold text-base text-secondary">
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-primary text-sm font-semibold truncate">{tx.merchant || "Unknown"}</p>
                        <p className="text-muted text-xs mt-0.5">
                          {new Date(tx.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <p className="text-sm font-bold flex-shrink-0" style={{ color: isDebit ? "var(--color-danger)" : "var(--color-success)" }}>
                        {isDebit ? "−" : "+"}₦{Number(tx.amount || 0).toLocaleString()}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-10 text-center">
                <div className="w-14 h-14 bg-surface-muted rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-file-list-3-line text-muted text-2xl" />
                </div>
                <p className="text-secondary font-semibold text-sm mb-1">No transactions yet</p>
                <p className="text-muted text-xs">Card transactions will appear here</p>
              </div>
            )}
          </div>
        </>
      )}

      <MobileBottomNav />
    </div>
  );
}
